import { memo, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { PublicationDetailProps } from '../../interfaces/components';
import { usePublicationDetail } from './hooks/usePublicationDetail';
import { usePublicationLogic } from './hooks/usePublicationLogic';
import { useResponsiveSlider } from './hooks/useResponsiveSlider';
import ServiceBoxs from '../ServiceBoxs';
import { Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { VehicleSchema, ProductSchema } from '../StructuredData/VehicleSchema';

// Componentes refactorizados
import PublicationHeader from './components/PublicationHeader/PublicationHeader';
import PublicationImageSlider from './components/PublicationImageSlider/PublicationImageSlider';
import PublicationCharacteristics from './components/PublicationCharacteristics/PublicationCharacteristics';
import PublicationDescription from './components/PublicationDescription/PublicationDescription';
import PublicationInformation from './components/PublicationInformation/PublicationInformation';
import PublicationVersionSelector from './components/PublicationVersionSelector/PublicationVersionSelector';
import PublicationDealershipSelector from './components/PublicationDealershipSelector/PublicationDealershipSelector';
import PublicationSimilars from './components/PublicationSimilars/PublicationSimilars';
import PublicationPromoBanners from './components/PublicationPromoBanners/PublicationPromoBanners';

// Componentes existentes
import CarBrandCharacteristics from '../CarBrandCharacteristics/CarBrandCharacteristics';
import ModalCharacteristics from './components/ModalCharacteristics/ModalCharacteristics';

import styles from './publicationDetail.module.scss';
import { PublicationPriceIndicator } from './components';
import { images } from '../../constants/images';
import { useNavigate } from 'react-router-dom';

const PublicationDetailRefactored = (props: PublicationDetailProps) => {
    const {
        type = "publication",
        publication,
        loading = false,
        clickFavorite = () => { },
        publicationsSimilars,
        children,
        buttonOff = false,
        handleDealerLogoClick,
        logoDealerships,
        onLinzeCheckClick,
        onAutoFinClick,
        vehicleTypeSelected,
        setVehicleTypeReducer,
        handlePressCotizar,
        publications,
        brand,
        brandsDealerships,
        selectedDealership,
        setSelectedDealership,
        selectedVersion,
        setSelectedVersion,
        user,
        setLoginRedirection,
        showNegotiationInProgress = false,
        offerAccepted,
        loginBancoChile,
        loginBancoChileData,
        loginBancoChileFetching,
        articleData
    } = props;

    const [showStickyButtons, setShowStickyButtons] = useState(false);
    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();


    useEffect(() => {
        const handleScroll = () => {
            if (buttonContainerRef.current) {
                const rect = buttonContainerRef.current.getBoundingClientRect();
                setShowStickyButtons(rect.bottom < 0);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (showStickyButtons) {
            document.body.classList.add('sticky-buttons-visible');
        } else {
            document.body.classList.remove('sticky-buttons-visible');
        }
        return () => {
            document.body.classList.remove('sticky-buttons-visible');
        };
    }, [showStickyButtons]);

    // Hooks personalizados
    const hookData = usePublicationDetail({ publication });
    const { sliderLimit } = useResponsiveSlider();
    const {
        getMinPrice,
        selectedVehicleData,
        openAdditionalModal,
        setOpenAdditionalModal,
        handleMokService,
        handleMaviService,
        handleFullMecService,
        handleIcarTagService,
        handleHDIService
    } = usePublicationLogic({
        publication,
        publications,
        brandsDealerships,
        selectedDealership,
        setSelectedDealership,
        selectedVersion,
        setSelectedVersion,
        setVehicleTypeReducer,
        articleData
    });

    const {
        isBonus,
        formData,
        selectedFeatures,
        getIndicatorPositionFromSuggested,
        getPriceDifferenceMessage,
        getIndicatorPosition,
        getPriceCategory,
        getPriceCategoryPublication,
        getIndicatorPositionPublication,
        hasValidSuggestedPrices,
        hasValidSuggestedPricesPublication
    } = publication && !loading ? hookData : {
        isBonus: false,
        formData: null,
        selectedFeatures: [],
        getIndicatorPositionFromSuggested: () => 0,
        getPriceDifferenceMessage: () => "",
        getIndicatorPosition: () => 0,
        getIndicatorPositionPublication: () => 0,
        getPriceCategory: () => "fair" as const,
        getPriceCategoryPublication: () => "fair" as const,
        hasValidSuggestedPrices: false,
        hasValidSuggestedPricesPublication: false
    };


    // Estado para saber si el indicador de precio está visible
    const [isPriceIndicatorVisible, setIsPriceIndicatorVisible] = useState<boolean>(false);

    // Determinar si el indicador de precio está visible
    useEffect(() => {
        const shouldShowPriceIndicator = publication?.status_brief?.code !== "sold_out" &&
            (type === 'myPublication' || type === 'publication' || type === 'salePost') &&
            hasValidSuggestedPricesPublication === true &&
            getPriceCategoryPublication !== undefined;

        setIsPriceIndicatorVisible(!!shouldShowPriceIndicator);
    }, [publication, type, hasValidSuggestedPricesPublication, getPriceCategoryPublication]);

    return (
        <>
            {/* Datos estructurados para SEO */}
            {publication && !loading && (
                <>
                    {vehicleTypeSelected === 'new' ? (
                        <ProductSchema publication={publication} />
                    ) : (
                        <VehicleSchema publication={publication} selectedVersion={selectedVersion} />
                    )}
                </>
            )}

            <PublicationHeader
                vehicleTypeSelected={vehicleTypeSelected}
                publication={publication}
                brand={brand}
                type={type}
                selectedVersion={selectedVersion}
                setSelectedVersion={setSelectedVersion}
                loading={loading}
                children={children}
                logoDealerships={logoDealerships}
                handleDealerLogoClick={handleDealerLogoClick}
                onLinzeCheckClick={onLinzeCheckClick}
                onAutoFinClick={onAutoFinClick}
                brandsDealerships={brandsDealerships}
                selectedDealership={selectedDealership}
                setSelectedDealership={setSelectedDealership}
                setOpenAdditionalModal={setOpenAdditionalModal}
                showNegotiationInProgress={showNegotiationInProgress}
            />

            <div className={styles.mainContent}>
                {/* Columna izquierda */}
                <div className={styles.leftColumn}>
                    <div className={brand ? styles.leftBoxBrand : styles.leftBox}>
                        <div className={styles.imageSliderContainer}>
                            <PublicationImageSlider
                                type={type}
                                brand={brand}
                                publication={publication}
                                selectedVersion={selectedVersion}
                                onLinzeCheckClick={onLinzeCheckClick}
                                vehicleTypeSelected={vehicleTypeSelected}
                                loading={loading}
                            />
                        </div>

                        <CarBrandCharacteristics
                            publication={publication}
                            selectedVersion={selectedVersion}
                            brand={brand}
                        />

                        <ModalCharacteristics
                            open={openAdditionalModal}
                            onClose={() => setOpenAdditionalModal(false)}
                            selectedVehicleId={selectedVersion || ''}
                            publication={publication}
                        />

                    </div>
                    <div className={type === 'myPublication' ? styles.serviceBoxMyPublication : styles.serviceBox}>
                        <ServiceBoxs
                            onIcarClick={(formData) => {
                                if (onLinzeCheckClick) {
                                    onLinzeCheckClick();
                                }
                            }}
                            onIcarTagClick={handleIcarTagService}
                            onMaviClick={handleMaviService}
                            onMokClick={handleMokService}
                            onFullMecClick={handleFullMecService}
                            onHDIClick={handleHDIService}
                            user={user}
                            setLoginRedirection={setLoginRedirection}
                            vehicleTypeSelected={vehicleTypeSelected}
                            brand={brand}
                            type={type}
                        />
                    </div>
                    <div className={styles.secondLeftBox}>
                        {!brand && (
                            <PublicationDescription
                                type={type}
                                publication={publication}
                                loading={loading}
                                selectedFeatures={selectedFeatures}
                            />
                        )}
                        <div
                            className={styles.additionalCharacteristicsLink}
                            onClick={() => setOpenAdditionalModal(true)}
                        >
                            Ver características adicionales
                        </div>
                    </div>
                </div>

                {/* Columna derecha */}
                <div className={styles.rightColumn}>
                    <div className={isPriceIndicatorVisible ? styles.rightBox : styles.rightBoxWithoutPriceIndicator}>
                        <div className={styles.rightBoxContent}>
                            <PublicationInformation
                                publication={publication}
                                loading={loading}
                                logoDealerships={logoDealerships}
                                handleDealerLogoClick={handleDealerLogoClick}
                                brand={brand}
                                type={type}
                                vehicleTypeSelected={vehicleTypeSelected}
                                getMinPrice={getMinPrice}
                                hasValidSuggestedPricesPublication={type === "salePost" ? hasValidSuggestedPrices : hasValidSuggestedPricesPublication}
                                getPriceCategoryPublication={type === "salePost" ? getPriceCategory : getPriceCategoryPublication}
                                selectedVehicleData={selectedVehicleData}
                                isPriceIndicatorVisible={isPriceIndicatorVisible}
                                loginBancoChile={loginBancoChile}
                                loginBancoChileData={loginBancoChileData}
                                loginBancoChileFetching={loginBancoChileFetching}
                                children={type === "myPublication" ? children : null}
                            />

                            <PublicationVersionSelector
                                publication={publication}
                                selectedVersion={selectedVersion}
                                setSelectedVersion={setSelectedVersion}
                                type={type}
                                vehicleTypeSelected={vehicleTypeSelected}
                                brand={brand}
                            />

                            <PublicationDealershipSelector
                                type={type}
                                vehicleTypeSelected={vehicleTypeSelected}
                                brand={brand}
                                brandsDealerships={brandsDealerships}
                                selectedDealership={selectedDealership}
                                setSelectedDealership={setSelectedDealership}
                            />
                        </div>

                        {/* Botones siempre al final del box */}
                        <div className={styles.bottomButtonContainer} ref={buttonContainerRef}>
                            {!loading && (
                                <>
                                    {type === "myPublication" ? (
                                        children
                                    ) : type !== "salePost" && vehicleTypeSelected === "used" ? (
                                        children
                                    ) : type !== "salePost" && vehicleTypeSelected === "new" ? (
                                        children
                                    ) : null}
                                </>
                            )}
                        </div>
                        {/* 
                        <PublicationPromoBanners
                            type={type}
                            vehicleTypeSelected={vehicleTypeSelected}
                            onLinzeCheckClick={onLinzeCheckClick}
                            onAutoFinClick={onAutoFinClick}
                        /> */}
                        {type === "salePost" && !loading && (
                            <div className={styles.buttonContainer}>
                                {children}
                            </div>
                        )}

                    </div>
                    {((type === "salePost" ? getPriceCategory() : getPriceCategoryPublication()) === "good" || (type === "salePost" ? getPriceCategory() : getPriceCategoryPublication()) === "fair") && (
                        <div className={styles.priceIndicatorContainer}>
                            <PublicationPriceIndicator
                                hasValidSuggestedPrices={type === "salePost" ? hasValidSuggestedPrices : hasValidSuggestedPricesPublication}
                                getPriceCategory={type === "salePost" ? getPriceCategory : getPriceCategoryPublication}
                                getIndicatorPosition={type === "salePost" ? getIndicatorPosition : getIndicatorPositionPublication}
                                getPriceDifferenceMessage={getPriceDifferenceMessage}
                                publication={publication}
                            />
                        </div>
                    )}
                    <div className={styles.pagoSeguro}>
                        <div className={styles.pagoSeguroLeft}>
                            <img src={images.pagoSeguro} alt="Secure Payment" width={28} height={22} />
                            <span className={styles.pagoSeguroText}>Pago seguro garantizado</span>
                        </div>
                        <img src={images.pagaEnLinea} alt="Paga en Línea" height={28} />
                    </div>
                </div>
            </div>

            <PublicationSimilars
                type={type}
                brand={brand}
                loading={loading}
                publications={publicationsSimilars}
                clickFavorite={clickFavorite}
                sliderLimit={sliderLimit}
            />

            <div className={`${styles.stickyButtonContainer} ${showStickyButtons ? styles.visible : ''}`}>
                <div className={styles.stickyPriceSection}>
                    <div className={styles.stickyLeftSection}>
                        <div className={styles.stickyPrice}>
                            <span className={offerAccepted ? styles.stickyPriceAmountOffer : styles.stickyPriceAmount}>$ {publication?.price?.toLocaleString()}</span>
                            {(getPriceCategoryPublication() === "good" || getPriceCategoryPublication() === "fair") && (
                                <div className={styles.priceIndicatorButtonsPublication} style={{ display: 'flex', alignItems: 'center' }}>
                                    <img
                                        src={
                                            getPriceCategoryPublication() === "good"
                                                ? images.checkVerde
                                                : images.checkAmarillo
                                        }
                                        alt="Indicador de precio"
                                        className={styles.priceIcon}
                                        style={{ width: '16px', height: '16px', marginRight: '4px' }}
                                    />
                                    {getPriceCategoryPublication() === "good"
                                        ? "Buen precio"
                                        : "Precio justo"}
                                </div>
                            )}
                        </div>

                    </div>
                    <div className={styles.stickyRightSection}>
                        <div className={styles.stickyButtonsRow}>
                            <div className={styles.stickyCreditLink} onClick={() => navigate('/financiar-auto')}>
                                <img src={images.iconCalculator} alt="Calcula tu crédito" title="Calcula tu crédito" />
                                <span>Calcula tu crédito</span>
                            </div>
                            <div className={styles.buttonSeparator}>|</div>
                            <div className={styles.stickyChildrenWrapper}>
                                {children}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(PublicationDetailRefactored);
